import { useState, useEffect, useCallback } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import type { PublisherDetail } from 'types/publisher';
import { getPublisher, getPublisherChannels, type PublisherChannel } from '../api/marketplaceApi';

export interface UsePublisherDetailResult {
  publisher: PublisherDetail | null;
  channels: PublisherChannel[];
  channelsTotal: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function usePublisherDetail(slug: string | null): UsePublisherDetailResult {
  const apiClients = useApiClientsOrNull();

  const [publisher, setPublisher] = useState<PublisherDetail | null>(null);
  const [channels, setChannels] = useState<PublisherChannel[]>([]);
  const [channelsTotal, setChannelsTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => setFetchTrigger((prev) => prev + 1), []);

  useEffect(() => {
    if (!apiClients || !slug) {
      setPublisher(null);
      setChannels([]);
      setChannelsTotal(0);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      getPublisher(apiClients.schemaClient, slug),
      getPublisherChannels(apiClients.schemaClient, slug),
    ])
      .then(([publisherRes, channelsRes]) => {
        if (!cancelled) {
          setPublisher(publisherRes.publisher);
          setChannels(channelsRes.channels);
          setChannelsTotal(channelsRes.total);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load publisher');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [apiClients, slug, fetchTrigger]);

  return { publisher, channels, channelsTotal, loading, error, refetch };
}
